import { useIntl } from 'react-intl';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { Auction } from '#src/atoms/icons/Auction.tsx';
import { Binoculars } from '#src/atoms/icons/Binoculars.tsx';
import { InstantBuy } from '#src/atoms/icons/InstantBuy.tsx';
import { Market } from '#src/atoms/icons/Market.tsx';
import { StarBall } from '#src/atoms/icons/StarBall/index.tsx';
import { Transfer } from '#src/atoms/icons/Transfer.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconList } from '#src/atoms/icons/system/iconList.ts';
import {
  ANY_SPORT_MARKET,
  ANY_SPORT_MARKET_SCOUT,
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
  ANY_SPORT_MARKET_WATCHLISTS,
  ANY_SPORT_SERIES_MARKET,
} from '#src/constants/__generated__/routes.ts';
import { useIsInTraining } from '#src/hooks/commonGame/useIsInTraining.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { useUnclaimedMarketTasksCount } from '#src/hooks/users/useUnclaimedMarketTasksCount.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import {
  glossary,
  navLabels,
  scoutingCenter,
  transferMarket,
} from '#src/lib/glossary.ts';
import { generateSportPath } from '#src/lib/routing/generateSportPath.ts';
import { ClickMarketHoverMenuButton_Target } from '#src/protos/events/so5/shared/events.ts';

import { NavItemWithSubNav } from '../NavItemWithSubNav/index.tsx';
import { useNavItemLayout } from '../useNavItemLayout.ts';

type Props = { sport: Sport; sidebarCollapsed?: boolean };

export const MarketNavItem = ({ sport, sidebarCollapsed }: Props) => {
  const { formatMessage } = useIntl();
  const { navItemsVariant, iconSize } = useNavItemLayout();
  const seriesNav = useSeriesNav();
  const isInTraining = useIsInTraining();
  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();
  const track = useEvents();

  const marketBadgeCount = {
    [Sport.FOOTBALL]: unclaimedMarketTasksCount,
    [Sport.BASEBALL]: 0,
    [Sport.NBA]: unclaimedMarketTasksCount,
  }[sport];

  const onClickFn = (t: ClickMarketHoverMenuButton_Target) => {
    return () => {
      track('Click Market Hover Menu Button', {
        sport,
        target: t,
      });
    };
  };

  return (
    <NavItemWithSubNav
      item={{
        variant: navItemsVariant,
        icon: ({ isActive }) => <Market size={iconSize} active={isActive} />,
        label: formatMessage(navLabels.transferMarket),
        locked: isInTraining,
        to: generateSportPath(
          seriesNav ? ANY_SPORT_SERIES_MARKET : ANY_SPORT_MARKET,
          {
            sport,
          }
        ),
        enabledStoredLastNavigation: !seriesNav,
        sidebarCollapsed,
        badge: {
          content: !seriesNav ? 0 : marketBadgeCount,
        },
        ...(!seriesNav && {
          subItems: [
            {
              icon: () => <StarBall width={16} height={16} />,
              label: formatMessage(glossary.explore),
              to: generateSportPath(ANY_SPORT_MARKET, {
                sport,
              }),
              end: true,
              subItems: [
                {
                  icon: () => <Auction />,
                  label: formatMessage(navLabels.auctions),
                  to: generateSportPath(ANY_SPORT_MARKET_SHOP_AUCTIONS, {
                    sport,
                  }),
                  onClick: onClickFn(
                    ClickMarketHoverMenuButton_Target.AUCTIONS
                  ),
                },
                {
                  icon: () => <InstantBuy size={2} />,
                  label: formatMessage(transferMarket.instantBuy),
                  to: generateSportPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY, {
                    sport,
                  }),
                  onClick: onClickFn(
                    ClickMarketHoverMenuButton_Target.INSTANT_BUY
                  ),
                },
                {
                  icon: () => <Transfer />,
                  label: formatMessage(transferMarket.transfer),
                  to: generateSportPath(ANY_SPORT_MARKET_SHOP_MANAGER_SALES, {
                    sport,
                  }),
                  onClick: onClickFn(
                    ClickMarketHoverMenuButton_Target.MANAGER_SALES
                  ),
                },
              ],
            },
            {
              icon: () => <Binoculars />,
              label: formatMessage(scoutingCenter.scout),
              to: generateSportPath(ANY_SPORT_MARKET_SCOUT, {
                sport,
              }),
            },
            {
              icon: () => <FontAwesomeIcon icon={iconList} size="1x" />,
              label: formatMessage(scoutingCenter.watchlists),
              to: generateSportPath(ANY_SPORT_MARKET_WATCHLISTS, {
                sport,
              }),
            },
          ],
        }),
      }}
    />
  );
};
